import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { SortableHead } from "@/components/spy-dashboard/SortableHead";
import { getChartSeriesColor } from "@/constants/chartColors";
import {
  formatGscCount,
  formatGscCtr,
  formatGscPosition,
} from "@/lib/gsc-display";
import { cn } from "@/lib/utils";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import {
  isGscDomainOverviewSortColumn,
  type GscDomainOverviewRow,
  type GscDomainOverviewSortColumn,
  type GscSortDirection,
} from "@/types/gsc";

interface DomainOverviewTableProps {
  rows: readonly GscDomainOverviewRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  sortBy: GscDomainOverviewSortColumn;
  sortDir: GscSortDirection;
  availableDomains: readonly string[];
  onPageChange: (page: number) => void;
  onSortChange: (column: GscDomainOverviewSortColumn) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
}

const DomainOverviewTableSkeleton = () => (
  <Card className="border-border shadow-none">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-28" />
    </CardHeader>
    <CardContent className="space-y-2 pt-0">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </CardContent>
  </Card>
);

const domainDotColor = (
  domain: string,
  availableDomains: readonly string[],
): string => {
  const index = availableDomains.indexOf(domain);
  return getChartSeriesColor(index >= 0 ? index : 0);
};

function DomainOverviewTableComponent({
  rows,
  totalCount,
  page,
  pageSize,
  sortBy,
  sortDir,
  availableDomains,
  onPageChange,
  onSortChange,
  isLoading = false,
  isFetching = false,
  className,
}: DomainOverviewTableProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleSort = (column: string): void => {
    if (!isGscDomainOverviewSortColumn(column)) return;
    onSortChange(column);
  };

  if (isLoading) {
    return (
      <div className={className} aria-busy aria-label="Loading domain overview">
        <DomainOverviewTableSkeleton />
      </div>
    );
  }

  const trackedLabel =
    totalCount === 1 ? "1 domain tracked" : `${totalCount} domains tracked`;

  return (
    <Card
      className={cn(
        "border-border shadow-none",
        isFetching && "pointer-events-none opacity-60",
        className,
      )}
      aria-busy={isFetching}
      aria-label="Domain overview"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold tracking-tight">
          Domain Overview
        </CardTitle>
        <span className="text-xs text-muted-foreground tabular-nums">
          {trackedLabel}
        </span>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="relative max-h-[min(28rem,60vh)] overflow-auto rounded-lg border border-border">
          <Table>
            <TableHeader className="sticky top-0 z-10 border-b border-border bg-muted/50 backdrop-blur-sm">
              <TableRow className="border-b border-border hover:bg-transparent">
                <SortableHead
                  label="Domain"
                  column="domain"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Clicks"
                  column="clicks"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Impressions"
                  column="impressions"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="CTR"
                  column="ctr"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Avg Position"
                  column="avg_position"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    No domain data for this period.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const color = domainDotColor(row.domain, availableDomains);

                  return (
                    <TableRow key={row.domain}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: color }}
                            aria-hidden
                          />
                          <CopyableValue value={row.domain} noTruncate />
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums tracking-tight">
                        {formatGscCount(row.clicks)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums tracking-tight">
                        {formatGscCount(row.impressions)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums tracking-tight">
                        {formatGscCtr(row.ctr)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums tracking-tight">
                        {formatGscPosition(row.avg_position)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 ? (
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

export const DomainOverviewTable = memo(DomainOverviewTableComponent);
DomainOverviewTable.displayName = "DomainOverviewTable";
